import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SessionClient } from '@cbcore/client/session';
import { UserClient } from '@cbcore/client/user';
import { UtilService } from '@cbcore/services/utils/utils/utils.service';
import { selectMicrositeSessions } from '@cbcore/NGRX/coreAuth/coreAuth.selectors';
import { AppState, callMicrositeSessions } from '@cbcore/NGRX/core.state';
import { setWebsocketEvent } from '@cbcore/NGRX/coreWebsockets/coreWebsockets.actions';

import { LanguageSwitcherComponent } from '@cbui/public_api';
import { HeaderAuthBoxComponent } from './header-auth-box/header-auth-box';

@Component({
  selector: 'header-component',
  templateUrl: './header.html',
  styleUrls: ['./header.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'wl-nav' },
  imports: [
    CommonModule,
    FormsModule,
    LanguageSwitcherComponent,
    HeaderAuthBoxComponent,
  ],
})
export class HeaderComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly _sessionClient = inject(SessionClient);
  private readonly _userClient = inject(UserClient);
  private readonly _router = inject(Router);
  private readonly store = inject(Store<AppState>);
  private readonly utilService = inject(UtilService);

  @Input() customizerVisible: boolean = false;
  @Output() toggleCustomizer = new EventEmitter<void>();

  public showBox: boolean = true;
  public isLoggedIn: boolean = false;
  public userInfo: any = undefined;
  public currencies: any = ['GBP', 'EUR', 'USD'];
  public user: any = {};
  public newUser: any = {
    currency_code: this.currencies[0],
    country_code: 'GB',
  };
  public isDarkTheme: boolean =
    document.documentElement.classList.contains('dark');

  toggleTheme() {
    const nextTheme = this.isDarkTheme ? 'light' : 'dark';
    this.utilService.setTheme(false, nextTheme);
    this.isDarkTheme = !this.isDarkTheme;
  }

  ngOnInit() {
    this.store
      .select(selectMicrositeSessions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((session) => {
        this.isLoggedIn = !!session.ext_id;
        this.showBox = false;
        this.userInfo = {
          id: session.id,
          ext_id: session.ext_id,
          manager_id: session.manager_id,
          currency_code: session.currency_code,
        };
      });
  }

  login() {
    this._sessionClient.login(this.user).subscribe(
      (res: any) => {
        this.user = {};
        this.store.dispatch(callMicrositeSessions());
        this.store.dispatch(
          setWebsocketEvent({
            event: 'USER_LOGIN',
            data: res.data,
          }),
        );
      },
      (_err: any) => alert('User not authenticated'),
    );
  }

  register() {
    const model = {
      user: this.newUser,
    };
    this._userClient.register(model).subscribe(
      (_res: any) => {
        this.user = {
          email: this.newUser.email,
          password: this.newUser.password,
        };
        this.login();
        this.newUser = {};
      },
      (_err: any) => alert('User not registered'),
    );
  }

  logout() {
    this._sessionClient.logout().subscribe(
      (res: any) => {
        this.store.dispatch(callMicrositeSessions());
        this._router.navigate(['/']);
        this.store.dispatch(
          setWebsocketEvent({
            event: 'USER_LOGOUT',
            data: res.data,
          }),
        );
      },
      (_err: any) => alert('Error on Logout, refresh the page'),
    );
  }
}
